import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { mediaUrl, isPrivatePath } from "@/lib/media";

const SEEN_KEY = "pp_popups_seen"; // { [popupId]: "AAAA-MM-JJ" } — simple marqueur, aucune donnée perso

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const readSeen = () => { try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "{}") || {}; } catch { return {}; } };
const markSeen = (id) => {
  try {
    const seen = readSeen();
    seen[id] = todayStr();
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch { /* ignore */ }
};

const pageKey = (pathname) => {
  if (pathname === "/" || pathname.startsWith("/permis/")) return "home";
  if (pathname === "/reserver") return "reserver";
  return "other"; // ne reçoit que les pop-ups « Toutes les pages »
};

const isInternal = (url) => url.startsWith("/") && !url.startsWith("//");

// Carte visuelle d'une pop-up (utilisée sur le site ET pour l'aperçu admin)
export function PopupCard({ popup, onClose, onCta, preview = false }) {
  const img = mediaUrl(popup.image_url);
  const cta = popup.cta_url ? (
    isInternal(popup.cta_url) && !preview ? (
      <Link to={popup.cta_url} onClick={onCta} data-testid="site-popup-cta"
        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand px-6 py-3.5 text-base font-bold text-white shadow-lg shadow-brand/20 hover:bg-brand-hover">
        {popup.cta_label || "J'en profite"} <ArrowRight className="h-5 w-5" />
      </Link>
    ) : (
      <a href={preview ? undefined : popup.cta_url} target="_blank" rel="noopener noreferrer" onClick={onCta} data-testid="site-popup-cta"
        className="mt-5 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-brand px-6 py-3.5 text-base font-bold text-white shadow-lg shadow-brand/20 hover:bg-brand-hover">
        {popup.cta_label || "J'en profite"} <ArrowRight className="h-5 w-5" />
      </a>
    )
  ) : null;
  return (
    <div className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}
      role="dialog" aria-modal="true" aria-labelledby="site-popup-title">
      <button type="button" aria-label="Fermer" onClick={onClose} data-testid="site-popup-close"
        className="absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-neutral-600 shadow hover:bg-white hover:text-neutral-900">
        <X className="h-5 w-5" />
      </button>
      {img && <img src={img} alt="" className="aspect-[16/9] w-full object-cover" data-testid="site-popup-image" />}
      <div className="p-6">
        <h2 id="site-popup-title" className="pr-8 text-2xl font-black leading-tight tracking-tight text-neutral-900">{popup.title || "Titre de la pop-up"}</h2>
        {popup.text && <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-neutral-600">{popup.text}</p>}
        {cta}
        <button type="button" onClick={onClose} data-testid="site-popup-dismiss" className="mt-2 w-full rounded-xl py-2.5 text-sm font-semibold text-neutral-500 hover:text-neutral-800">
          Non merci
        </button>
      </div>
    </div>
  );
}

// Gestionnaire global : 1 seule pop-up à la fois, 1 fois par visiteur et par jour, pages publiques uniquement.
export default function SitePopupManager() {
  const { pathname } = useLocation();
  const [popup, setPopup] = useState(null);
  const shownThisPage = useRef(false);

  useEffect(() => {
    setPopup(null);
    shownThisPage.current = false;
    if (isPrivatePath(pathname)) return undefined;
    let cancelled = false;
    const cleanups = [];

    api.get("/public/popups", { params: { page: pageKey(pathname) } }).then(({ data }) => {
      if (cancelled || !Array.isArray(data)) return;
      const seen = readSeen();
      const today = todayStr();
      const candidate = data.find((p) => seen[p.id] !== today);
      if (!candidate) return;

      const show = () => {
        if (cancelled || shownThisPage.current) return;
        shownThisPage.current = true;
        markSeen(candidate.id);
        setPopup(candidate);
      };

      const touch = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(hover: none)").matches;
      if (candidate.trigger === "exit" && !touch) {
        // Intention de sortie : la souris quitte la fenêtre par le haut (onglets / barre d'adresse).
        const onOut = (e) => { if (!e.relatedTarget && e.clientY <= 0) show(); };
        document.addEventListener("mouseout", onOut);
        cleanups.push(() => document.removeEventListener("mouseout", onOut));
      } else {
        // Délai choisi ; sur mobile (pas d'intention de sortie détectable) : 20 s par défaut.
        const secs = candidate.trigger === "exit" ? 20 : Math.max(0, Number(candidate.delay_seconds) || 0);
        const t = setTimeout(show, secs * 1000);
        cleanups.push(() => clearTimeout(t));
      }
    }).catch(() => {});

    return () => { cancelled = true; cleanups.forEach((fn) => fn()); };
  }, [pathname]);

  useEffect(() => {
    if (!popup) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setPopup(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [popup]);

  if (!popup) return null;
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 p-4" onClick={() => setPopup(null)} data-testid="site-popup" data-popup-id={popup.id}>
      <PopupCard popup={popup} onClose={() => setPopup(null)} onCta={() => setPopup(null)} />
    </div>
  );
}
